// 棚卸し（stocktake）— Stocky parity v2 Step 2。運用は Starter 以上。
// 履歴と Stocky CSV の源泉値は Free でも read-only で閲覧できる。
// 開始すると expected（開始時点のシステム在庫）がその場で固定され、以後変わらない。
// 適用は **絶対値セット**＝カウント中に売れた分を二重計上しない。Shopify の在庫が
// 動くので実行前に window.confirm を挟む（在庫調整・「注文済みにする」と同じ idiom）。
import {
  Autocomplete,
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  Checkbox,
  DataTable,
  InlineStack,
  Layout,
  Page,
  Select,
  Text,
  TextField,
} from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiError } from "../api/client";
import {
  STOCKTAKE_STATUS_LABELS,
  type Stocktake,
  type StocktakeAppliedTarget,
  type StocktakeApplicationBlocker,
  type StocktakeApplicationPreview,
  type StocktakeApplicationTarget,
  applyStocktake,
  cancelStocktake,
  getStocktakeCandidateCount,
  getStocktakeApplicationPreview,
  getStocktake,
  listStocktakes,
  setStocktakeCounts,
  startStocktake,
  updateStocktakeNote,
  updateStocktakeSourceMetadata,
} from "../api/stocktakes";
import { listStocktakeHistoryLocationOptions } from "../api/stocky_csv_import";
import type { StockyPublicBigIntId } from "../api/stocky_public_ids";
import { variantMatchLabel } from "../api/variants";
import { getPlanPreview } from "../api/plan_previews";
import { CsvImportPanel } from "../components/CsvImportPanel";
import { PlanDataPreviewGate } from "../components/PlanDataPreviewGate";
import { QueryErrorBanner } from "../components/QueryErrorBanner";
import { ScanInput } from "../components/ScanInput";
import { usePlan } from "../hooks/usePlan";
import { useVerifiedShopGeneration } from "../ShopGenerationBoundary";

import { t } from "../lib/i18n";
import { formatDateTime } from "../utils/date";
import { downloadTextFile } from "../utils/downloadFile";
import {
  idempotencyAttempt,
  clearPersistentIdempotencyAttempt,
  persistentIdempotencyAttempt,
  type IdempotencyAttempt,
  type PersistentIdempotencyAttempt,
} from "../utils/idempotency";
import { parseQuantityInput } from "../utils/quantityInput";
import {
  differingRecordedProductTitle,
  hasCurrentProductTitleProjection,
  merchantSafeRecordedProductTitle,
  projectedProductTitle,
} from "../utils/productTitles";
import {
  countEnteredItems,
  filterStocktakeItems,
} from "../utils/stocktakeCounting";
import {
  buildStocktakeCountInputCsv,
  stocktakeCountCsvSpec,
} from "./stocktakeCountCsvSpec";

const STOCKTAKES_KEY = ["stocktakes"];

function savedStocktakeItemName(item: Stocktake["items"][number]): string {
  const safeRecorded = merchantSafeRecordedProductTitle(item);
  const name = safeRecorded ?? item.sku ?? String(item.shopify_variant_id);
  const sku = item.sku && safeRecorded ? `（SKU: ${item.sku}）` : "";
  return `${name}${sku}`;
}

function itemName(item: Stocktake["items"][number]): string {
  if (!hasCurrentProductTitleProjection(item)) {
    return savedStocktakeItemName(item);
  }
  const current = projectedProductTitle(item) ?? t("(名称未設定)");
  const sku = item.sku ? `（SKU: ${item.sku}）` : "";
  const recorded = differingRecordedProductTitle(item);
  const recordedLabel = recorded
    ? ` [${t("保存時の商品バリエーション名")}: ${recorded}]`
    : "";
  return `${current}${sku}${recordedLabel}`;
}

function stocktakeSearchLabel(item: Stocktake["items"][number]): string {
  const current = hasCurrentProductTitleProjection(item)
    ? (projectedProductTitle(item) ?? t("(名称未設定)"))
    : (merchantSafeRecordedProductTitle(item) ?? t("(名称未設定)"));
  const recorded = differingRecordedProductTitle(item);
  const title = recorded
    ? `${current} [${t("保存時の商品バリエーション名")}: ${recorded}]`
    : current;
  return t("{title} / SKU: {sku} / Shopify バリアントID: {id}", {
    title,
    sku: item.sku ?? "—",
    id: item.shopify_variant_id,
  });
}

function varianceLabel(variance: number | null): string {
  if (variance === null) return "—";
  if (variance > 0) return `+${variance}`;
  return String(variance);
}

function operationErrorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.detail;
  if (error instanceof Error) return error.message;
  return t(
    "予期しないエラーが発生しました。時間をおいて、もう一度お試しください。",
  );
}

function isDefinitiveClientFailure(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  // API契約上、副作用前の入力拒否と断定できるstatusだけをclearする。
  // 認証・認可・競合・rate-limit・未分類4xxは結果が曖昧なので保持する。
  return [400, 404, 422].includes(error.status);
}

function applicationSourceLabel(
  item: Pick<
    StocktakeApplicationTarget | StocktakeApplicationBlocker,
    "source_title" | "source_sku" | "source_shopify_variant_id"
  >,
): string {
  return t("{title} / SKU: {sku} / Shopify バリアントID: {id}", {
    title: item.source_title?.trim() || t("(名称未設定)"),
    sku: item.source_sku?.trim() || "—",
    id: item.source_shopify_variant_id,
  });
}

function applicationTargetLabel(target: StocktakeApplicationTarget): string {
  const variantTitle = target.target_variant_title?.trim();
  const productAndVariant =
    variantTitle && variantTitle !== "Default Title"
      ? `${target.target_product_title} / ${variantTitle}`
      : target.target_product_title;
  return t("{title} / SKU: {sku} / Shopify バリアントID: {id}", {
    title: productAndVariant,
    sku: target.target_sku?.trim() || "—",
    id: target.target_shopify_variant_id,
  });
}

function appliedTargetSourceLabel(target: StocktakeAppliedTarget): string {
  return t(
    "{title} / SKU: {sku} / 保存時のShopify バリアントID: {variantId} / 棚卸し明細ID: {itemId}",
    {
      title: target.source_title?.trim() || t("(名称未設定)"),
      sku: target.source_sku?.trim() || "—",
      variantId: target.source_shopify_variant_id,
      itemId: target.stocktake_item_id,
    },
  );
}

function appliedTargetDestinationLabel(target: StocktakeAppliedTarget): string {
  const variantTitle = target.target_variant_title?.trim();
  const productAndVariant =
    variantTitle && variantTitle !== "Default Title"
      ? `${target.target_product_title} / ${variantTitle}`
      : target.target_product_title;
  return t(
    "{title} / SKU: {sku} / Shopify 商品ID: {productId} / バリアントID: {variantId} / 在庫アイテムID: {inventoryItemId} / ロケーションID: {locationId}",
    {
      title: productAndVariant || t("(名称未設定)"),
      sku: target.target_sku?.trim() || "—",
      productId: target.target_shopify_product_id,
      variantId: target.target_shopify_variant_id,
      inventoryItemId: target.target_shopify_inventory_item_id,
      locationId: target.target_shopify_location_id,
    },
  );
}

function applicationTargetConfirmationLabel(
  target: StocktakeApplicationTarget,
): string {
  const variantTitle = target.target_variant_title?.trim();
  const productAndVariant =
    variantTitle && variantTitle !== "Default Title"
      ? `${target.target_product_title} / ${variantTitle}`
      : target.target_product_title;
  return `${productAndVariant} / ${target.target_sku?.trim() || "—"}`;
}

const APPLICATION_BLOCKER_MESSAGES: Record<string, string> = {
  source_product_missing:
    "この棚卸しを保存したときの商品を確認できません。現在の商品を確認してから、もう一度お試しください。",
  source_identity_mismatch:
    "保存された棚卸し商品と元の商品情報が一致しません。安全のため在庫へ反映しません。",
  source_inventory_tracking_disabled:
    "現在のShopify商品で在庫追跡が無効です。Shopifyで在庫追跡を有効にしてから、もう一度お試しください。",
  current_product_not_confirmed:
    "現在のShopifyで同じ商品だと確認された商品がありません。保存商品で現在の商品を確認してから、もう一度お試しください。",
  mapping_source_identity_mismatch:
    "棚卸しを保存したときの商品と、現在の商品を確認した記録が一致しません。安全のため在庫へ反映しません。",
  confirmed_current_product_missing:
    "以前に同じ商品だと確認したShopify商品が現在は見つかりません。現在の商品を確認し直してください。",
  confirmed_current_product_changed:
    "同じ商品だと確認した後にShopify商品が更新されています。現在の商品を確認し直してください。",
  current_product_inventory_unavailable:
    "現在のShopify商品の在庫情報を確認できません。Shopifyの商品設定を確認してください。",
  duplicate_current_product_target:
    "複数の棚卸し商品が同じShopify商品を指しています。どの実数を使うか確認してください。",
  current_product_unavailable:
    "現在のShopify商品を確認できないため、安全に在庫へ反映できません。",
};

function applicationBlockerMessage(reasonCode: string): string {
  const message = APPLICATION_BLOCKER_MESSAGES[reasonCode];
  return message
    ? t(message)
    : t(
        "安全に反映できない理由を確認できません。画面を更新しても解決しない場合は、表示された商品を添えてお問い合わせください。",
      );
}

function applicationPlanFingerprint(
  preview: StocktakeApplicationPreview,
): string {
  const status = [
    "pending_recovery",
    "reconciliation_required",
    "finalizing",
  ].includes(preview.status)
    ? "recovery"
    : preview.status;
  return JSON.stringify({
    stocktake_id: preview.stocktake_id,
    status,
    confirmation_token: preview.confirmation_token,
    targets: preview.targets.map((target) => ({
      stocktake_item_id: target.stocktake_item_id,
      source_shopify_variant_id: target.source_shopify_variant_id,
      source_sku: target.source_sku,
      source_title: target.source_title,
      counted_qty: target.counted_qty,
      target_shopify_product_id: target.target_shopify_product_id,
      target_shopify_variant_id: target.target_shopify_variant_id,
      target_sku: target.target_sku,
      target_product_title: target.target_product_title,
      target_variant_title: target.target_variant_title,
      uses_confirmed_current_product: target.uses_confirmed_current_product,
    })),
  });
}

function applicationIntentFingerprint(
  preview: StocktakeApplicationPreview,
): string {
  return JSON.stringify({
    stocktake_id: preview.stocktake_id,
    targets: preview.targets.map((target) => ({
      stocktake_item_id: target.stocktake_item_id,
      source_shopify_variant_id: target.source_shopify_variant_id,
      counted_qty: target.counted_qty,
      target_shopify_product_id: target.target_shopify_product_id,
      target_shopify_variant_id: target.target_shopify_variant_id,
    })),
  });
}

function StocktakeApplicationPanel({
  preview,
  busy,
  onConfirm,
  onRefresh,
  onClose,
}: {
  preview: StocktakeApplicationPreview;
  busy: boolean;
  onConfirm: () => void;
  onRefresh: () => void;
  onClose: () => void;
}) {
  const progress = t(
    "{completed} / {total} 件（内部処理 {chunks} / {allChunks}）",
    {
      completed: preview.completed_items.toLocaleString(),
      total: preview.total_items.toLocaleString(),
      chunks: preview.completed_chunks,
      allChunks: preview.total_chunks,
    },
  );
  const targetRows = preview.targets.map((target) => [
    applicationSourceLabel(target),
    applicationTargetLabel(target),
    String(target.counted_qty),
  ]);

  if (preview.status === "blocked") {
    return (
      <Banner tone="critical" title={t("この棚卸しはまだ在庫へ反映できません")}>
        <BlockStack gap="300">
          {preview.blockers.map((blocker) => (
            <BlockStack gap="100" key={blocker.stocktake_item_id}>
              <Text as="p" fontWeight="semibold">
                {applicationSourceLabel(blocker)} /{" "}
                {t("実数 {count}", { count: blocker.counted_qty })}
              </Text>
              <Text as="p">
                {applicationBlockerMessage(blocker.reason_code)}
              </Text>
            </BlockStack>
          ))}
          <InlineStack gap="200">
            <Button onClick={onRefresh} loading={busy} disabled={busy}>
              {t("反映内容を再確認")}
            </Button>
            <Button variant="plain" onClick={onClose} disabled={busy}>
              {t("閉じる")}
            </Button>
          </InlineStack>
        </BlockStack>
      </Banner>
    );
  }

  if (preview.status === "reconciliation_required") {
    return (
      <Banner
        tone="critical"
        title={t("Shopifyへの在庫反映結果を照合してください")}
      >
        <BlockStack gap="200">
          <Text as="p">
            {t(
              "Shopifyへ送信した結果を自動で確定できませんでした。同じ在庫変更を新しく送らず、照合画面でShopifyの実在庫を確認してください。",
            )}
          </Text>
          <Text as="p">{t("処理済み: {progress}", { progress })}</Text>
          <InlineStack gap="200">
            <Button url="/inventory-reconciliation" variant="primary">
              {t("在庫反映の照合を開く")}
            </Button>
            <Button onClick={onRefresh} loading={busy} disabled={busy}>
              {t("状態を更新")}
            </Button>
          </InlineStack>
        </BlockStack>
      </Banner>
    );
  }

  const recovering = preview.status === "pending_recovery";
  const finalizing = preview.status === "finalizing";
  const confirmationRequired = preview.status === "confirmation_required";
  const title = recovering
    ? t("同じ在庫反映を安全に再開できます")
    : finalizing
      ? t("Shopifyへの送信は完了し、最終同期を待っています")
      : t("Shopifyへ反映する内容を確認してください");

  return (
    <Banner tone={recovering || finalizing ? "warning" : "info"} title={title}>
      <BlockStack gap="300">
        {recovering && (
          <Text as="p">
            {t(
              "途中まで完了した同じ内容だけを再開します。別の商品や数量を新しく送ることはありません。",
            )}
          </Text>
        )}
        {finalizing && (
          <Text as="p">
            {t(
              "Shopifyの実在庫を読み直し、UreyukiBoxの履歴と在庫表示を最終確定します。新しい在庫変更は送りません。",
            )}
          </Text>
        )}
        {(recovering || finalizing || preview.total_chunks > 1) && (
          <Text as="p">
            {t("進捗: {progress}", { progress })}
            {preview.total_chunks > 1
              ? ` ${t(
                  "この操作1回で対象をすべて反映します。UreyukiBoxが内部で分割して続行するため、操作を分けたり繰り返したりする必要はありません。",
                )}`
              : ""}
          </Text>
        )}
        {confirmationRequired &&
          preview.targets
            .filter((target) => target.uses_confirmed_current_product)
            .map((target) => (
              <Text as="p" key={target.stocktake_item_id}>
                {t(
                  "この棚卸しの商品は、現在のShopify商品「{target}」と同じ商品だと確認できています。入力した実数「{count}」を、このShopify商品の在庫へ反映しますか？",
                  {
                    target: applicationTargetConfirmationLabel(target),
                    count: target.counted_qty,
                  },
                )}
              </Text>
            ))}
        {targetRows.length > 0 && (
          <DataTable
            columnContentTypes={["text", "text", "numeric"]}
            headings={[
              t("棚卸しを保存したときの商品"),
              t("現在のShopify商品"),
              t("反映する実数"),
            ]}
            rows={targetRows}
          />
        )}
        <InlineStack gap="200">
          <Button
            variant="primary"
            tone="critical"
            onClick={onConfirm}
            loading={busy}
            disabled={busy}
          >
            {recovering
              ? t("同じ内容で再開する")
              : finalizing
                ? t("最終同期を再開する")
                : t("確認した内容を在庫へ反映する")}
          </Button>
          <Button variant="plain" onClick={onClose} disabled={busy}>
            {t("閉じる")}
          </Button>
        </InlineStack>
      </BlockStack>
    </Banner>
  );
}

function importedStatusLabel(
  imported: boolean | undefined,
  status: string,
): string {
  if (imported) {
    return t("Stockyから取り込み済み（Stocky側の適用状態は不明）");
  }
  return t(
    STOCKTAKE_STATUS_LABELS[status as keyof typeof STOCKTAKE_STATUS_LABELS] ??
      status,
  );
}

function sourceInterpretationLabel(
  value: Stocktake["source_quantity_interpretation"],
): string {
  if (value === "counted") {
    return t("商品を数えた後の在庫数として保存");
  }
  if (value === "expected") {
    return t("商品を数える前の Stocky 在庫数として保存");
  }
  if (value === "unknown") {
    return t("分からない（CSV の値をそのまま保存）");
  }
  return t("指定なし（手動列対応）");
}

function StocktakeNoteBlock({
  note,
  imported = false,
}: {
  note: string | null;
  imported?: boolean;
}) {
  if (!note?.trim()) {
    return (
      <Text as="p" tone="subdued">
        {t("メモなし")}
      </Text>
    );
  }
  return (
    <BlockStack gap="100">
      <Text as="p" fontWeight="medium">
        {t("メモ")}
      </Text>
      <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
        {note}
      </div>
      {imported ? (
        <Text as="p" variant="bodySm" tone="subdued">
          {t(
            "このメモはUreyukiBoxの棚卸しメモです。Stockyでの棚卸し名やCSV原本の内容とは別に保存されています。",
          )}
        </Text>
      ) : null}
    </BlockStack>
  );
}

function AppliedTargetEvidenceBlock({ stocktake }: { stocktake: Stocktake }) {
  if (
    stocktake.status !== "applied" ||
    stocktake.imported_from_stocky === true
  ) {
    return null;
  }
  const evidence = stocktake.applied_target_evidence;
  if (evidence.status !== "available") {
    return (
      <Banner tone="warning" title={t("実際の反映先を正確に表示できません")}>
        <p>
          {t(
            "保存した反映記録がないか、内容を安全に確認できません。現在の商品情報から推測せず、反映先を表示していません。",
          )}
        </p>
      </Banner>
    );
  }

  const rows = evidence.targets.map((target) => {
    const destination = appliedTargetDestinationLabel(target);
    return [
      appliedTargetSourceLabel(target),
      target.confirmed_as_same_current_product
        ? `${destination}（${t(
            "このShopify商品を同じ商品として確認して反映しました",
          )}）`
        : destination,
      String(target.counted_qty),
    ];
  });
  return (
    <BlockStack gap="200">
      <Text as="h3" variant="headingSm">
        {t("Shopifyへ実際に反映した内容")}
      </Text>
      <Text as="p" tone="subdued">
        {t(
          "反映時に保存した記録です。現在の商品情報から推測した内容ではありません。",
        )}
      </Text>
      <DataTable
        columnContentTypes={["text", "text", "numeric"]}
        headings={[
          t("保存した棚卸し商品"),
          t("実際に反映したShopify商品"),
          t("反映した実数"),
        ]}
        rows={rows}
      />
    </BlockStack>
  );
}

function HistoryDetailCard({
  stocktakeId,
  onClose,
}: {
  stocktakeId: StockyPublicBigIntId;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [editingSource, setEditingSource] = useState(false);
  const [sourceDate, setSourceDate] = useState("");
  const [sourceLocationId, setSourceLocationId] = useState("");
  const [sourceUpdateId, setSourceUpdateId] = useState("");
  const detailQuery = useQuery({
    queryKey: ["stocktake", stocktakeId],
    queryFn: () => getStocktake(stocktakeId),
  });
  const sourceLocationsQuery = useQuery({
    queryKey: ["stocktake-history-location-options"],
    queryFn: listStocktakeHistoryLocationOptions,
    enabled: editingSource && detailQuery.data?.imported_from_stocky === true,
  });
  const sourceMetadataMutation = useMutation({
    mutationFn: () =>
      updateStocktakeSourceMetadata(stocktakeId, {
        update_id: sourceUpdateId,
        expected_revision: detailQuery.data?.source_metadata_revision ?? 0,
        counted_on: sourceDate || null,
        location_id: sourceLocationId || null,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(["stocktake", stocktakeId], updated);
      void queryClient.invalidateQueries({ queryKey: STOCKTAKES_KEY });
      setEditingSource(false);
    },
  });

  const beginSourceEdit = () => {
    const current = detailQuery.data;
    if (!current) return;
    setSourceDate(current.source_counted_on ?? "");
    setSourceLocationId(
      current.source_location_id == null
        ? ""
        : String(current.source_location_id),
    );
    setSourceUpdateId(crypto.randomUUID());
    sourceMetadataMutation.reset();
    setEditingSource(true);
  };

  const sourceEditError = sourceMetadataMutation.error
    ? sourceMetadataMutation.error instanceof ApiError
      ? sourceMetadataMutation.error.detail
      : t(
          "棚卸しの実施日・拠点を更新できませんでした。もう一度お試しください。",
        )
    : null;

  if (detailQuery.isError) {
    return (
      <QueryErrorBanner
        title={t("棚卸し履歴の詳細取得に失敗しました")}
        onRetry={() => detailQuery.refetch()}
      />
    );
  }
  const stocktake = detailQuery.data;
  if (!stocktake) {
    return (
      <Card>
        <Text as="p" tone="subdued">
          {t("棚卸し履歴の詳細を読み込んでいます…")}
        </Text>
      </Card>
    );
  }

  const imported = stocktake.imported_from_stocky === true;
  const rows = stocktake.items.map((item) => {
    const common = [
      savedStocktakeItemName(item),
      item.expected_qty === null ? "—" : String(item.expected_qty),
      item.counted_qty === null ? "—" : String(item.counted_qty),
      varianceLabel(item.variance),
    ];
    if (!imported) return common;
    return [
      ...common,
      item.source_current_stock == null
        ? "—"
        : String(item.source_current_stock),
      item.source_adjustment_qty == null
        ? "—"
        : String(item.source_adjustment_qty),
    ];
  });

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">
            {t("棚卸し履歴の詳細")}
          </Text>
          <Button variant="plain" onClick={onClose}>
            {t("閉じる")}
          </Button>
        </InlineStack>
        {imported && (
          <Banner tone="info" title={t("Stockyから取り込み済み")}>
            <p>
              {t(
                "Stocky側で在庫へ適用済みかどうかは、このCSVからは確認できません。UreyukiBoxは現在在庫を変更していません。",
              )}
            </p>
          </Banner>
        )}
        <StocktakeNoteBlock note={stocktake.note} imported={imported} />
        <AppliedTargetEvidenceBlock stocktake={stocktake} />
        {imported && (
          <BlockStack gap="200">
            <Text as="p">
              {t("元CSV: {file}", {
                file: stocktake.source_file_name ?? "—",
              })}
            </Text>
            {stocktake.stocky_stocktake_list && (
              <BlockStack gap="100">
                <Text as="p" fontWeight="medium">
                  {t("Stocky棚卸し一覧から保存した情報")}
                </Text>
                <Text as="p">
                  {t("Stocky No.: {number}", {
                    number: stocktake.stocky_stocktake_list.number_raw,
                  })}
                </Text>
                <Text as="p">
                  {t("Stockyでの棚卸し名: {name}", {
                    name: stocktake.stocky_stocktake_list.name_raw || t("不明"),
                  })}
                </Text>
                <Text as="p">
                  {t("Stocky一覧の拠点: {location}", {
                    location:
                      stocktake.stocky_stocktake_list.location_raw || t("不明"),
                  })}
                </Text>
                <Text as="p">
                  {t("Stockyでの作成日時: {generatedAt}", {
                    generatedAt:
                      stocktake.stocky_stocktake_list.generated_at.local_iso?.replace(
                        "T",
                        " ",
                      ) ?? stocktake.stocky_stocktake_list.generated_at.raw,
                  })}
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  {t(
                    "作成日時はStockyのGenerated atを正規化した値で、棚卸しの完了日時ではありません。Stockyでの棚卸し名もコメントとは別に保存しています。",
                  )}
                </Text>
              </BlockStack>
            )}
            {editingSource ? (
              <BlockStack gap="200">
                <TextField
                  label={t("実施日（不明なら空欄）")}
                  type="date"
                  value={sourceDate}
                  onChange={(value) => {
                    setSourceDate(value);
                    setSourceUpdateId(crypto.randomUUID());
                    sourceMetadataMutation.reset();
                  }}
                  autoComplete="off"
                  disabled={sourceMetadataMutation.isPending}
                />
                <Select
                  label={t("実施拠点")}
                  options={[
                    { label: t("拠点を選択してください"), value: "" },
                    ...(sourceLocationsQuery.data ?? []).map((location) => ({
                      label: location.active
                        ? location.name
                        : t("{name}（無効）", { name: location.name }),
                      value: String(location.id),
                    })),
                  ]}
                  value={sourceLocationId}
                  onChange={(value) => {
                    setSourceLocationId(value);
                    setSourceUpdateId(crypto.randomUUID());
                    sourceMetadataMutation.reset();
                  }}
                  disabled={sourceMetadataMutation.isPending}
                  helpText={t("選ばない場合は不明のまま保存します。")}
                />
                {sourceEditError && (
                  <Banner
                    tone="critical"
                    title={t("実施日・拠点を更新できませんでした")}
                  >
                    <p>{sourceEditError}</p>
                  </Banner>
                )}
                <InlineStack gap="200" align="end">
                  <Button
                    onClick={() => setEditingSource(false)}
                    disabled={sourceMetadataMutation.isPending}
                  >
                    {t("キャンセル")}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => sourceMetadataMutation.mutate()}
                    loading={sourceMetadataMutation.isPending}
                    disabled={!sourceUpdateId}
                  >
                    {t("実施日・拠点を保存")}
                  </Button>
                </InlineStack>
              </BlockStack>
            ) : (
              <Button onClick={beginSourceEdit}>
                {t("実施日・拠点を設定・修正")}
              </Button>
            )}
            {(stocktake.source_metadata_history?.length ?? 0) > 0 && (
              <BlockStack gap="100">
                <Text as="p" fontWeight="medium">
                  {t("実施日・拠点の記録履歴")}
                </Text>
                {stocktake.source_metadata_history?.map((entry, index) => (
                  <Text
                    as="p"
                    variant="bodySm"
                    tone="subdued"
                    key={`${entry.changed_at}-${index}`}
                  >
                    {t(
                      "実施日 {date}、拠点 {location}（記録日時: {changedAt}）",
                      {
                        date: entry.counted_on ?? t("不明"),
                        location: entry.location_name ?? t("不明"),
                        changedAt: formatDateTime(entry.changed_at),
                      },
                    )}
                  </Text>
                ))}
              </BlockStack>
            )}
            <Text as="p">
              {t("実施日: {date}", {
                date: stocktake.source_counted_on ?? t("不明"),
              })}
            </Text>
            <Text as="p">
              {t("実施拠点: {name}", {
                name: stocktake.source_location_name ?? t("不明"),
              })}
            </Text>
            <Text as="p">
              {t("Current Stock の扱い: {meaning}", {
                meaning: sourceInterpretationLabel(
                  stocktake.source_quantity_interpretation,
                ),
              })}
            </Text>
          </BlockStack>
        )}
        {rows.length === 0 ? (
          <Text as="p" tone="subdued">
            {t("明細はありません。")}
          </Text>
        ) : (
          <DataTable
            columnContentTypes={
              imported
                ? [
                    "text",
                    "numeric",
                    "numeric",
                    "numeric",
                    "numeric",
                    "numeric",
                  ]
                : ["text", "numeric", "numeric", "numeric"]
            }
            headings={
              imported
                ? [
                    t("商品"),
                    t("システム在庫"),
                    t("実数"),
                    t("差異"),
                    "Current Stock",
                    "Adjustment",
                  ]
                : [t("商品"), t("システム在庫"), t("実数"), t("差異")]
            }
            rows={rows}
          />
        )}
      </BlockStack>
    </Card>
  );
}

/** カウント中の棚卸し1件＝実数入力表。 */
function CountingCard({
  stocktake,
  candidateTotal,
  candidateBatchSize,
  onApplied,
}: {
  stocktake: Stocktake;
  candidateTotal?: number;
  candidateBatchSize?: number;
  onApplied: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [noteDraft, setNoteDraft] = useState(stocktake.note ?? "");
  const [noteSaved, setNoteSaved] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [applicationPreview, setApplicationPreview] =
    useState<StocktakeApplicationPreview | null>(null);
  const [csvRefetching, setCsvRefetching] = useState(false);
  const applyAttempt = useRef<IdempotencyAttempt | null>(null);
  // 棚卸しは「リストを潰し切る」作業なのに、残りが何件でどれなのかが画面に無かった。
  const [searchInput, setSearchInput] = useState("");
  const [selectedSearchIds, setSelectedSearchIds] = useState<string[]>([]);
  const [uncountedOnly, setUncountedOnly] = useState(false);
  // 仕様は棚卸しIDに紐づく＝ID が変わらないかぎり作り直さない
  // （毎レンダーで新しい object を渡すとパネルの内部状態が落ちる）。
  const countCsvSpec = useMemo(
    () => stocktakeCountCsvSpec(stocktake.id),
    [stocktake.id],
  );
  const downloadCountInputCsv = useCallback(() => {
    downloadTextFile(
      `ureyukibox-stocktake-${stocktake.id}-counts.csv`,
      buildStocktakeCountInputCsv(stocktake.items),
    );
  }, [stocktake.id, stocktake.items]);
  const handleCountsImported = useCallback(() => {
    // CSVの本適用が成功したら、手入力中の値と混ぜない。サーバーの完成応答を
    // 取得し直すまでは反映確認を無効にし、古い表からpreviewを作らせない。
    setDrafts({});
    setApplicationPreview(null);
    setCsvRefetching(true);
    void Promise.all([
      queryClient.refetchQueries({
        queryKey: ["stocktake", stocktake.id],
        exact: true,
        type: "active",
      }),
      queryClient.invalidateQueries({ queryKey: STOCKTAKES_KEY }),
    ]).finally(() => setCsvRefetching(false));
  }, [queryClient, stocktake.id]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: STOCKTAKES_KEY });
    queryClient.invalidateQueries({ queryKey: ["stocktake", stocktake.id] });
  };
  const onError = (err: unknown) => setErrorMessage(operationErrorMessage(err));

  const saveMutation = useMutation({
    mutationFn: (
      counts: {
        shopify_variant_id: StockyPublicBigIntId;
        counted_qty: number;
      }[],
    ) => setStocktakeCounts(stocktake.id, counts),
    onSuccess: (updated) => {
      setErrorMessage(null);
      setDrafts({});
      setApplicationPreview(null);
      // 保存応答がこの操作の真値。再取得を待って古いpropsを表示し続けない。
      queryClient.setQueryData(["stocktake", stocktake.id], updated);
      void queryClient.invalidateQueries({ queryKey: STOCKTAKES_KEY });
    },
    onError,
  });
  const noteMutation = useMutation({
    mutationFn: () =>
      updateStocktakeNote(
        stocktake.id,
        noteDraft.trim() === "" ? null : noteDraft.trim(),
      ),
    onSuccess: (updated) => {
      setErrorMessage(null);
      setNoteDraft(updated.note ?? "");
      setNoteSaved(true);
      queryClient.setQueryData(["stocktake", stocktake.id], updated);
      void queryClient.invalidateQueries({ queryKey: STOCKTAKES_KEY });
    },
    onError: (error) => {
      setNoteSaved(false);
      onError(error);
    },
  });
  const previewMutation = useMutation({
    mutationFn: () => getStocktakeApplicationPreview(stocktake.id),
    onMutate: () => {
      setErrorMessage(null);
      setApplicationPreview(null);
    },
    onSuccess: (preview) => setApplicationPreview(preview),
    onError,
  });
  const applyMutation = useMutation({
    mutationFn: (args: {
      stocktakeId: StockyPublicBigIntId;
      idempotencyKey: string;
      confirmationToken: string | null;
    }) =>
      applyStocktake(
        args.stocktakeId,
        args.idempotencyKey,
        args.confirmationToken,
      ),
    onSuccess: (updated) => {
      applyAttempt.current = null;
      setErrorMessage(null);
      setApplicationPreview(null);
      queryClient.setQueryData(["stocktake", stocktake.id], updated);
      onApplied(t("棚卸しを適用しました。Shopify の在庫にも反映済みです。"));
      invalidate();
    },
    onError,
    // Idempotency-Key により、応答喪失・一時的な5xxだけは同じ操作を1回再試行できる。
    // 4xxは入力/状態競合なので自動再試行しない。
    retry: (failureCount, error) =>
      failureCount < 1 && (!(error instanceof ApiError) || error.status >= 500),
    retryDelay: 250,
  });
  const verifyPreviewMutation = useMutation({
    mutationFn: (_displayed: StocktakeApplicationPreview) =>
      getStocktakeApplicationPreview(stocktake.id),
    onSuccess: (latest, displayed) => {
      setApplicationPreview(latest);
      if (
        latest.status === "blocked" ||
        latest.status === "reconciliation_required"
      ) {
        setErrorMessage(null);
        return;
      }
      if (
        applicationPlanFingerprint(latest) !==
        applicationPlanFingerprint(displayed)
      ) {
        if (
          applicationIntentFingerprint(latest) !==
          applicationIntentFingerprint(displayed)
        ) {
          applyAttempt.current = null;
        }
        setErrorMessage(
          t(
            "確認後に商品または実数が変わりました。更新した内容を確認してから、もう一度実行してください。",
          ),
        );
        return;
      }
      const fingerprint = applicationIntentFingerprint(latest);
      const attempt = idempotencyAttempt(applyAttempt.current, fingerprint);
      applyAttempt.current = attempt;
      applyMutation.mutate({
        stocktakeId: stocktake.id,
        idempotencyKey: attempt.key,
        confirmationToken:
          latest.status === "confirmation_required"
            ? latest.confirmation_token
            : null,
      });
    },
    onError,
  });
  const cancelMutation = useMutation({
    mutationFn: () => cancelStocktake(stocktake.id),
    onSuccess: () => {
      setErrorMessage(null);
      onApplied(t("棚卸しを中止しました。在庫は変更していません。"));
      invalidate();
    },
    onError,
  });

  // 未保存の入力＋保存済みカウントの合計＝適用対象の件数。
  // 🔴 `parseQuantityInput` を通す＝素の parseInt だと貼り付けた "1,200" が 1 になり、
  // 「入力した数がそのまま在庫数になる」適用で在庫を 1 に上書きしてしまう。
  const parsedDrafts = Object.entries(drafts).map(([id, raw]) => ({
    id,
    parsed: parseQuantityInput(raw),
  }));
  const pending = parsedDrafts.flatMap((entry) =>
    entry.parsed.kind === "value"
      ? [{ id: entry.id, value: entry.parsed.value }]
      : [],
  );
  // 読めなかった入力は黙って捨てない（捨てると保存件数が静かに減る）。
  const invalidDraftCount = parsedDrafts.filter(
    (entry) => entry.parsed.kind === "invalid",
  ).length;
  const savedCount = stocktake.items.filter(
    (item) => item.counted_qty !== null,
  ).length;
  const hasDrafts = parsedDrafts.length > 0;
  const applicationBusy =
    saveMutation.isPending ||
    csvRefetching ||
    previewMutation.isPending ||
    verifyPreviewMutation.isPending ||
    applyMutation.isPending ||
    cancelMutation.isPending;

  const handleSave = () => {
    if (pending.length === 0 || invalidDraftCount > 0) return;
    saveMutation.mutate(
      pending.map((entry) => ({
        shopify_variant_id: entry.id,
        counted_qty: entry.value,
      })),
    );
  };

  const handlePreview = () => {
    if (savedCount === 0 || hasDrafts || applicationBusy) return;
    previewMutation.mutate();
  };

  const handleConfirmedApply = () => {
    if (applicationPreview === null || hasDrafts || applicationBusy) return;
    verifyPreviewMutation.mutate(applicationPreview);
  };

  const handleCancel = () => {
    const ok = window.confirm(
      t(
        "この棚卸しを中止します。入力した実数は破棄され、在庫は変更されません。よろしいですか？",
      ),
    );
    if (!ok) return;
    cancelMutation.mutate();
  };

  // スキャン1回＝実数 +1。棚に並んだ物を順に読む運用なので、同じ商品を続けて
  // 読んだら足し上がるのが正しい（読むたびに 1 で上書きされると数え直しになる）。
  const handleScanned = (shopifyVariantId: StockyPublicBigIntId) => {
    const canonicalId = String(shopifyVariantId);
    setApplicationPreview(null);
    setDrafts((prev) => {
      const item = stocktake.items.find(
        (i) => String(i.shopify_variant_id) === canonicalId,
      );
      // 表示中の値（未保存の入力 → 保存済みの実数 → 0）を起点に足す。
      const current =
        prev[canonicalId] ??
        (item?.counted_qty === null || item?.counted_qty === undefined
          ? ""
          : String(item.counted_qty));
      const parsed = Number.parseInt(current, 10);
      const base = Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
      return { ...prev, [canonicalId]: String(base + 1) };
    });
  };

  // 進捗＝保存済みだけでなく打ち込み中も「数え終わった」に数える（保存前に
  // 「まだ残っている」と言われると残件数が使い物にならない）。
  const enteredCount = countEnteredItems(stocktake.items, drafts);
  const remainingCount = stocktake.items.length - enteredCount;
  // 「全部入力済み」と言ってよいのは検索で絞っていないとき（空振りと区別する）。
  const allCounted = remainingCount === 0 && searchInput.trim() === "";
  const matchingItems = filterStocktakeItems(
    stocktake.items,
    { query: searchInput, uncountedOnly },
    drafts,
  );
  const selectedSearchId = selectedSearchIds[0];
  const visibleItems = selectedSearchId
    ? filterStocktakeItems(
        stocktake.items,
        { query: "", uncountedOnly },
        drafts,
      ).filter((item) => String(item.shopify_variant_id) === selectedSearchId)
    : matchingItems;
  const searchOptions = (selectedSearchId ? visibleItems : matchingItems).map(
    (item) => ({
      value: String(item.shopify_variant_id),
      label: stocktakeSearchLabel(item),
    }),
  );

  const rows = visibleItems.map((item) => [
    itemName(item),
    item.expected_qty === null ? "—" : String(item.expected_qty),
    <TextField
      key={item.shopify_variant_id}
      label={t("{item} の実数", { item: itemName(item) })}
      labelHidden
      type="integer"
      autoComplete="off"
      value={
        drafts[String(item.shopify_variant_id)] ??
        (item.counted_qty === null ? "" : String(item.counted_qty))
      }
      onChange={(value) => {
        setApplicationPreview(null);
        setDrafts((prev) => ({
          ...prev,
          [String(item.shopify_variant_id)]: value,
        }));
      }}
      disabled={applicationBusy}
    />,
    varianceLabel(item.variance),
  ]);

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">
            {t("カウント中の棚卸し")}
          </Text>
          <Badge tone="attention">{t(STOCKTAKE_STATUS_LABELS.counting)}</Badge>
        </InlineStack>
        <Text as="p" tone="subdued">
          {t(
            "システム在庫は開始した時点の数で固定されています。カウント中に商品が売れても、適用時には入力した実数がそのまま在庫数になります（増減の足し算ではありません）。",
          )}
        </Text>
        <BlockStack gap="200">
          <TextField
            label={t("メモ（任意）")}
            value={noteDraft}
            onChange={(value) => {
              setNoteDraft(value);
              setNoteSaved(false);
            }}
            autoComplete="off"
            multiline={3}
            maxLength={2000}
            helpText={t(
              "この棚卸しのメモを編集できます。保存後は履歴にも表示されます。",
            )}
          />
          <InlineStack gap="200" blockAlign="center">
            <Button
              onClick={() => noteMutation.mutate()}
              loading={noteMutation.isPending}
              disabled={
                noteMutation.isPending ||
                noteDraft.trim() === (stocktake.note?.trim() ?? "")
              }
            >
              {t("メモを保存")}
            </Button>
            {noteSaved && (
              <Text as="span" tone="success">
                {t("メモを保存しました。")}
              </Text>
            )}
          </InlineStack>
        </BlockStack>
        {candidateTotal !== undefined &&
          candidateBatchSize !== undefined &&
          candidateTotal > candidateBatchSize && (
            <Banner tone="info" title={t("商品数が多いため範囲を分けています")}>
              <p>
                {t(
                  "処理を安定させるため、現在の商品全{total}件を{batchSize}件ごとの範囲に分けています。新しい棚卸しを開始するときに範囲を選んでください。",
                  {
                    total: candidateTotal.toLocaleString(),
                    batchSize: candidateBatchSize.toLocaleString(),
                  },
                )}
              </p>
            </Banner>
          )}
        {errorMessage && (
          <Banner
            tone="critical"
            title={t("棚卸しの操作に失敗しました")}
            onDismiss={() => setErrorMessage(null)}
          >
            {errorMessage}
          </Banner>
        )}
        <ScanInput
          disabled={applicationBusy}
          onResolved={(variant) => handleScanned(variant.shopify_variant_id)}
          validate={(variant) =>
            stocktake.items.some(
              (item) =>
                String(item.shopify_variant_id) ===
                String(variant.shopify_variant_id),
            )
              ? null
              : // 開始時に固定した対象の外を読んだ＝黙って捨てると数え漏らしになる。
                t(
                  "この棚卸しの対象に含まれていない商品です（{item}）。棚卸しを中止して開始し直すと最新の商品が対象になります。",
                  {
                    item: variantMatchLabel(variant),
                  },
                )
          }
          helpText={t(
            "読み取るたびに、その商品の実数が 1 ずつ増えます。読み終えたら「実数を保存」を押してください。",
          )}
        />
        {/* スキャンは1件ずつ＝数千 SKU の店では現実的でない。同じ「実数を埋める」
            操作を CSV でも受ける（在庫は動かさない点はどちらも同じ）。 */}
        <CsvImportPanel
          spec={countCsvSpec}
          onImported={handleCountsImported}
          beforeFileInput={
            <Banner tone="info" title={t("CSVの形式")}>
              <BlockStack gap="200">
                <Text as="p">
                  {t(
                    "必須: 実数の列と、商品を特定する Shopify バリアントID または SKU のどちらか1列。両方を対応付けた場合は Shopify バリアントID を使います。",
                  )}
                </Text>
                <Text as="p">
                  {t(
                    "自動認識する商品列: Shopify ID / shopify_id / Variant ID / Shopify Variant ID、SKU / 品番 / 商品コード。実数列: Counted / Count / 実数 / 実棚 / カウント / 棚卸数 / Actual。別の見出しも、読み込み後に列を選べます。",
                  )}
                </Text>
                <Text as="p">
                  {t("例: Shopify ID,SKU,Counted / 8001,HONEY-500,24")}
                </Text>
                <Text as="p">
                  {t(
                    "文字コードは UTF-8（BOM可）、Shift_JIS / CP932、Windows-1252、BOM付きUTF-16に対応します。上限は5MBです。",
                  )}
                </Text>
                <Text as="p">
                  {t(
                    "空の実数は未カウントのままです。小数は切り上げます。負数・読めない値・この範囲にない商品は取り込まず、同じIDまたはSKUが複数行にある商品も合算や後勝ちにせず除外します。確認画面で件数とエラーを確認してから実数だけを保存し、この時点では在庫を変更しません。",
                  )}
                </Text>
                <Text as="p" tone="subdued">
                  {t(
                    "入力用CSVには現在の棚卸し範囲の Shopify ID、SKU、保存済みの実数だけを入れます。画面でまだ保存していない入力は含みません。",
                  )}
                </Text>
                <InlineStack align="start">
                  <Button onClick={downloadCountInputCsv}>
                    {t("現在の範囲を入力用CSVでダウンロード")}
                  </Button>
                </InlineStack>
              </BlockStack>
            </Banner>
          }
        />

        {/* 🔴 残りが何件でどれなのかを出す＝棚卸しは潰し切る作業なので、
            これが無いと倉庫に立っている人が目視で行を追うことになる。 */}
        <BlockStack gap="300">
          <InlineStack gap="300" blockAlign="center">
            <Text as="span" fontWeight="semibold">
              {t(
                "実数を入力した商品 {done} / {total} 件（残り {remaining} 件）",
                {
                  done: enteredCount,
                  total: stocktake.items.length,
                  remaining: remainingCount,
                },
              )}
            </Text>
            <Checkbox
              label={t("未入力だけ表示")}
              checked={uncountedOnly}
              onChange={setUncountedOnly}
            />
          </InlineStack>
          <Autocomplete
            options={searchOptions}
            selected={selectedSearchIds}
            onSelect={(selected) => {
              const selectedId = selected[0];
              const item = stocktake.items.find(
                (candidate) =>
                  String(candidate.shopify_variant_id) === selectedId,
              );
              setSelectedSearchIds(selectedId ? [selectedId] : []);
              if (item) setSearchInput(stocktakeSearchLabel(item));
            }}
            listTitle={t("棚卸し対象の商品候補")}
            emptyState={
              <Text as="p" tone="subdued">
                {t("条件に一致する商品はありません")}
              </Text>
            }
            textField={
              <Autocomplete.TextField
                label={t("商品を検索")}
                value={searchInput}
                onChange={(value) => {
                  setSearchInput(value);
                  setSelectedSearchIds([]);
                }}
                placeholder={t("商品名 / SKU / Shopify バリアントID")}
                autoComplete="off"
                clearButton
                onClearButtonClick={() => {
                  setSearchInput("");
                  setSelectedSearchIds([]);
                }}
              />
            }
          />
        </BlockStack>

        {stocktake.items.length === 0 ? (
          // 絞り込みのせいではない＝「絞り込みを解除」を出すと嘘の行き先になる。
          <Banner
            tone="warning"
            title={t("この棚卸しには商品が1件もありません")}
          >
            <p>
              {t(
                "商品が同期される前に開始された可能性があります。中止して、同期が終わってから開始し直してください。",
              )}
            </p>
          </Banner>
        ) : rows.length === 0 ? (
          // 🔴 「全部入力済み」と言えるのは検索していないときだけ＝検索が空振り
          //    しただけなのに「すべて入力しました」と出すと、数え残しを見落とす。
          <Banner
            tone={allCounted ? "success" : "info"}
            title={
              allCounted
                ? t("すべての商品に実数を入力しました")
                : t("条件に一致する商品はありません")
            }
            action={{
              content: t("絞り込みを解除"),
              onAction: () => {
                setSearchInput("");
                setUncountedOnly(false);
                setSelectedSearchIds([]);
              },
            }}
          >
            <p>
              {allCounted
                ? t(
                    "「入力した実数を保存」を押してから「在庫へ反映する内容を確認」で対象商品と実数を確認します。",
                  )
                : t("商品名・SKU の一部で探せます。")}
            </p>
          </Banner>
        ) : (
          <DataTable
            columnContentTypes={["text", "numeric", "numeric", "numeric"]}
            headings={[t("商品"), t("システム在庫"), t("実数"), t("差異")]}
            rows={rows}
          />
        )}
        {invalidDraftCount > 0 && (
          <Banner tone="warning">
            {t(
              "{count} 件の実数が数値として読めないため保存できません。整数で入力してください（例: 1200）。",
              { count: String(invalidDraftCount) },
            )}
          </Banner>
        )}
        {hasDrafts && (
          <Banner tone="info">
            {invalidDraftCount > 0
              ? t(
                  "読めない実数を修正してから保存してください。未保存の入力がある間は、在庫へ反映する内容を確認できません。",
                )
              : t(
                  "未保存の実数があります。「入力した実数を保存」後に、在庫へ反映する内容を確認できます。",
                )}
          </Banner>
        )}
        {csvRefetching && (
          <Banner tone="info">
            {t(
              "CSVから保存した実数を読み直しています。最新の表が表示されるまで、在庫への反映は開始できません。",
            )}
          </Banner>
        )}
        {applicationPreview && (
          <StocktakeApplicationPanel
            preview={applicationPreview}
            busy={applicationBusy}
            onConfirm={handleConfirmedApply}
            onRefresh={handlePreview}
            onClose={() => setApplicationPreview(null)}
          />
        )}
        <InlineStack gap="300">
          <Button
            onClick={handleSave}
            disabled={
              pending.length === 0 || invalidDraftCount > 0 || applicationBusy
            }
            loading={saveMutation.isPending}
          >
            {t("入力した実数を保存")}
          </Button>
          <Button
            variant="primary"
            onClick={handlePreview}
            disabled={savedCount === 0 || hasDrafts || applicationBusy}
            loading={previewMutation.isPending}
          >
            {t("在庫へ反映する内容を確認")}
          </Button>
          <Button
            tone="critical"
            variant="plain"
            onClick={handleCancel}
            loading={cancelMutation.isPending}
            disabled={applicationBusy || cancelMutation.isPending}
          >
            {t("棚卸しを中止")}
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

export function Stocktakes() {
  const queryClient = useQueryClient();
  const { shopGeneration } = useVerifiedShopGeneration();
  const plan = usePlan();
  const canOperate =
    !plan.isLoading &&
    !plan.isError &&
    plan.isProductLimitExceeded !== true &&
    plan.allows("stocktake");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [candidateOffset, setCandidateOffset] = useState("0");
  const startAttempt = useRef<PersistentIdempotencyAttempt | null>(null);
  const [selectedHistoryId, setSelectedHistoryId] =
    useState<StockyPublicBigIntId | null>(null);

  const listQuery = useQuery({
    queryKey: STOCKTAKES_KEY,
    queryFn: listStocktakes,
  });

  const summaries = listQuery.data ?? [];
  const active = summaries.find((s) => s.status === "counting");

  const candidatesQuery = useQuery({
    queryKey: ["stocktake-candidate-count"],
    queryFn: getStocktakeCandidateCount,
    enabled: canOperate,
  });
  const candidateTotal = candidatesQuery.data?.total ?? 0;
  const candidateBatchSize = candidatesQuery.data?.batch_size ?? 1000;
  const candidateRangeOptions = Array.from(
    { length: Math.ceil(candidateTotal / candidateBatchSize) },
    (_, index) => {
      const offset = index * candidateBatchSize;
      const end = Math.min(offset + candidateBatchSize, candidateTotal);
      return {
        value: String(offset),
        label: t("商品 {from}〜{to} / {total} 件", {
          from: offset + 1,
          to: end,
          total: candidateTotal,
        }),
      };
    },
  );
  // 同期で商品数が減った場合、以前選んだ末尾範囲が Select の候補外に残ると
  // クリック時に 422 になる。現存する範囲へ戻して、無効値を送らない。
  useEffect(() => {
    const selectedOffset = Number(candidateOffset);
    if (
      candidateTotal > 0 &&
      candidateBatchSize > 0 &&
      (!Number.isInteger(selectedOffset) ||
        selectedOffset < 0 ||
        selectedOffset % candidateBatchSize !== 0 ||
        selectedOffset >= candidateTotal)
    ) {
      setCandidateOffset("0");
    }
  }, [candidateBatchSize, candidateOffset, candidateTotal]);

  // カウント中の1件だけ明細を取りに行く（履歴は件数だけで足りる）。
  const activeQuery = useQuery({
    queryKey: ["stocktake", active?.id],
    queryFn: () => getStocktake(active!.id),
    enabled: canOperate && active !== undefined,
  });

  const startMutation = useMutation({
    mutationFn: ({
      input,
      attempt,
    }: {
      input: Parameters<typeof startStocktake>[0];
      attempt: PersistentIdempotencyAttempt;
    }) => startStocktake(input, attempt.key),
    onSuccess: (created, variables) => {
      clearPersistentIdempotencyAttempt(
        "stocktake:create",
        variables.attempt,
      );
      startAttempt.current = null;
      // 🔴 上限で載らなかった商品がある＝このカウント表は「全商品」ではない。
      // 黙ると、載らなかった商品の在庫差異を棚卸ししたつもりで取りこぼす。
      const offset = created.candidate_offset ?? 0;
      const total = created.candidate_total ?? created.items.length;
      const end = Math.min(offset + created.items.length, total);
      setSuccessMessage(
        total > created.items.length
          ? t(
              "棚卸しを開始しました（商品 {from}〜{to} / {total} 件）。この範囲を反映後、次の範囲を選んでください。",
              { from: offset + 1, to: end, total },
            )
          : t("棚卸しを開始しました。システム在庫を記録しています。"),
      );
      setStartError(null);
      setNote("");
      queryClient.invalidateQueries({ queryKey: STOCKTAKES_KEY });
    },
    onError: (err: unknown, variables) => {
      if (isDefinitiveClientFailure(err)) {
        clearPersistentIdempotencyAttempt(
          "stocktake:create",
          variables.attempt,
        );
        startAttempt.current = null;
      }
      setSuccessMessage(null);
      setStartError(err instanceof Error ? err.message : String(err));
    },
  });

  const historyRows = summaries.map((s) => [
    s.imported_from_stocky
      ? (s.source_counted_on ?? t("不明"))
      : formatDateTime(s.started_at),
    s.imported_from_stocky
      ? (s.source_location_name ?? t("不明"))
      : (s.location_name ?? "—"),
    importedStatusLabel(s.imported_from_stocky, s.status),
    s.imported_from_stocky && s.source_quantity_interpretation === "unknown"
      ? t("源泉値 {count} 件", { count: s.item_count })
      : `${s.counted_count} / ${s.item_count}`,
    s.imported_from_stocky ? "—" : formatDateTime(s.applied_at),
    s.source_file_name ?? "—",
    s.note?.trim() ? s.note : t("メモなし"),
    <Button
      key={s.id}
      variant="plain"
      onClick={() => setSelectedHistoryId(s.id)}
    >
      {t("詳細を見る")}
    </Button>,
  ]);

  return (
    <Page title={t("棚卸し")}>
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {listQuery.isError && (
              <QueryErrorBanner
                title={t("棚卸し履歴の取得に失敗しました")}
                onRetry={() => listQuery.refetch()}
              />
            )}
            <PlanDataPreviewGate
              feature="stocktake"
              featureName={t("棚卸しの操作")}
              previewQueryKey={["plan-preview", "stocktake-candidates"]}
              previewQueryFn={async () => [
                await getPlanPreview("stocktake_candidates"),
              ]}
              sections={[
                {
                  dataset: "stocktake_candidates",
                  title: t("棚卸し対象の商品"),
                  columns: [
                    { key: "product", label: t("商品") },
                    { key: "sku", label: "SKU" },
                    {
                      key: "expected_qty",
                      label: t("システム在庫"),
                    },
                    { key: "counted_qty", label: t("実数") },
                    { key: "variance", label: t("差異") },
                  ],
                },
              ]}
            >
              <BlockStack gap="400">
                {successMessage && (
                  <Banner
                    tone="success"
                    onDismiss={() => setSuccessMessage(null)}
                  >
                    {successMessage}
                  </Banner>
                )}
                {startError && (
                  <Banner
                    tone="critical"
                    title={t("棚卸しを開始できませんでした")}
                    onDismiss={() => setStartError(null)}
                  >
                    {startError}
                  </Banner>
                )}
                {(activeQuery.isError || candidatesQuery.isError) && (
                  <QueryErrorBanner
                    title={t("棚卸しの操作情報の取得に失敗しました")}
                    onRetry={() => {
                      activeQuery.refetch();
                      candidatesQuery.refetch();
                    }}
                  />
                )}
                {active === undefined && (
                  <Card>
                    <BlockStack gap="400">
                      <Text as="h2" variant="headingMd">
                        {t("棚卸しを開始する")}
                      </Text>
                      <Text as="p" tone="subdued">
                        {t(
                          "主拠点の商品を一覧にして、いまのシステム在庫を記録します。数え終えて「在庫へ反映する内容を確認」で対象商品と実数を確認し、確定すると Shopify の在庫が実数に置き換わります — Shopify 管理画面で数量を直接編集するのと同じ効果です。同じ棚卸しを両方で行うと二重に反映されるため、どちらか一方で行ってください。",
                        )}
                      </Text>
                      {candidateRangeOptions.length > 1 && (
                        <Select
                          label={t("棚卸しする商品範囲")}
                          options={candidateRangeOptions}
                          value={candidateOffset}
                          onChange={setCandidateOffset}
                          helpText={t(
                            "1回に最大1,000件です。1つの範囲を反映したら、次の範囲を開始してください。",
                          )}
                        />
                      )}
                      <TextField
                        label={t("メモ（任意）")}
                        value={note}
                        onChange={setNote}
                        autoComplete="off"
                        maxLength={2000}
                        helpText={t(
                          "最大2,000文字。開始後も履歴と詳細で確認できます。",
                        )}
                      />
                      <div>
                        <Button
                          variant="primary"
                          onClick={() => {
                            const input = {
                              note:
                                note.trim() === "" ? undefined : note.trim(),
                              candidate_offset: Number(candidateOffset),
                            };
                            const attempt = persistentIdempotencyAttempt({
                              scope: "stocktake:create",
                              shopGeneration,
                              previous: startAttempt.current,
                              fingerprint: JSON.stringify(input),
                            });
                            startAttempt.current = attempt;
                            startMutation.mutate({ input, attempt });
                          }}
                          loading={
                            startMutation.isPending || candidatesQuery.isLoading
                          }
                          disabled={
                            candidateTotal === 0 || candidatesQuery.isError
                          }
                        >
                          {t("棚卸しを開始する")}
                        </Button>
                      </div>
                    </BlockStack>
                  </Card>
                )}
                {active !== undefined && activeQuery.data && (
                  <CountingCard
                    key={active.id}
                    stocktake={activeQuery.data}
                    candidateTotal={candidatesQuery.data?.total}
                    candidateBatchSize={candidatesQuery.data?.batch_size}
                    onApplied={(message) => {
                      setSuccessMessage(message);
                      setStartError(null);
                    }}
                  />
                )}
              </BlockStack>
            </PlanDataPreviewGate>
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  {t("棚卸し履歴")}
                </Text>
                <Text as="p" tone="subdued">
                  {t(
                    "UreyukiBoxで実施した棚卸しと、Stockyから取り込んだ履歴です。StockyのCSVは在庫への適用状態を証明しないため、取り込み済みと適用済みを分けて表示します。Shopify管理画面で直接編集した変動はここには並びません。",
                  )}
                </Text>
                {historyRows.length === 0 ? (
                  <Text as="p" tone="subdued">
                    {t("棚卸しの履歴はまだありません。")}
                  </Text>
                ) : (
                  <DataTable
                    columnContentTypes={[
                      "text",
                      "text",
                      "text",
                      "text",
                      "text",
                      "text",
                      "text",
                      "text",
                    ]}
                    headings={[
                      t("実施日時"),
                      t("拠点"),
                      t("状態"),
                      t("カウント / 源泉値"),
                      t("UreyukiBoxで適用"),
                      t("元CSV"),
                      t("メモ"),
                      t("詳細"),
                    ]}
                    rows={historyRows}
                  />
                )}
              </BlockStack>
            </Card>
            {selectedHistoryId !== null && (
              <HistoryDetailCard
                stocktakeId={selectedHistoryId}
                onClose={() => setSelectedHistoryId(null)}
              />
            )}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
